"use client"

import * as React from "react"
import * as TogglePrimitive from "@radix-ui/react-toggle"
import { Toggle } from "../../components/toggle"
import { UITypography } from "../display/ui-typography"
import { cn } from "../../lib/utils"
import { cva, type VariantProps } from "class-variance-authority"
import { Sun, Moon, type LucideIcon } from "lucide-react"
import { UIBox } from "../layout/ui-box"

// ============================================================================
// UIToggle Types
// ============================================================================

export interface UIToggleProps extends VariantProps<typeof uiToggleVariants> {
  /**
   * 토글 상태 (controlled)
   */
  pressed?: boolean
  /**
   * 기본 토글 상태 (uncontrolled)
   */
  defaultPressed?: boolean
  /**
   * 상태 변경 콜백
   */
  onPressedChange?: (pressed: boolean) => void
  /**
   * 비활성화 여부
   */
  disabled?: boolean
  /**
   * 토글 내용
   */
  children?: React.ReactNode
  /**
   * 아이콘 (선택사항)
   */
  icon?: LucideIcon
  /**
   * 아이콘 위치
   */
  iconPosition?: 'left' | 'right'
  /**
   * aria-label (접근성)
   */
  'aria-label'?: string
  /**
   * 컨테이너 클래스명
   */
  className?: string
  /**
   * 타입 (단일 또는 그룹)
   */
  type?: 'single' | 'multiple'
}

const uiToggleVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium transition-[background-color,color,border-color,box-shadow] duration-250 disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-transparent hover:bg-muted data-[state=on]:bg-accent data-[state=on]:text-accent-foreground hover:text-muted-foreground",
        outline: "border border-input bg-transparent hover:bg-accent hover:text-accent-foreground data-[state=on]:bg-accent data-[state=on]:text-accent-foreground data-[state=on]:border-accent shadow-sm",
        ghost: "hover:bg-accent hover:text-accent-foreground data-[state=on]:bg-accent data-[state=on]:text-accent-foreground",
        accent: "bg-accent/10 text-accent hover:bg-accent/20 data-[state=on]:bg-accent data-[state=on]:text-accent-foreground border border-accent/20 data-[state=on]:border-accent",
        solid: "bg-secondary text-secondary-foreground hover:bg-secondary/80 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground shadow-sm",
      },
      size: {
        sm: "h-8 px-2 gap-1 text-xs",
        default: "h-9 px-3 gap-2",
        lg: "h-10 px-4 gap-2",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

// ============================================================================
// UIToggle Component
// ============================================================================

export const UIToggle = React.forwardRef<
  React.ComponentRef<typeof TogglePrimitive.Root>,
  UIToggleProps
>(({
  pressed,
  defaultPressed = false,
  onPressedChange,
  disabled,
  children,
  icon: Icon,
  iconPosition = 'left',
  variant,
  size,
  className,
  type = 'single',
  ...props
}, ref) => {
  // Icon-only 모드
  if (!children && Icon) {
    return (
      <Toggle
        ref={ref}
        pressed={pressed}
        defaultPressed={defaultPressed}
        onPressedChange={onPressedChange}
        disabled={disabled}
        className={cn(uiToggleVariants({ variant, size: 'icon' }), className)}
        {...props}
      >
        <Icon className="h-4 w-4" />
      </Toggle>
    )
  }

  // Icon with text 모드
  if (children && Icon) {
    return (
      <Toggle
        ref={ref}
        pressed={pressed}
        defaultPressed={defaultPressed}
        onPressedChange={onPressedChange}
        disabled={disabled}
        className={cn(uiToggleVariants({ variant, size }), className)}
        {...props}
      >
        {iconPosition === 'left' && <Icon className="h-4 w-4" />}
        {children}
        {iconPosition === 'right' && <Icon className="h-4 w-4" />}
      </Toggle>
    )
  }

  // Children-only 모드
  return (
    <Toggle
      ref={ref}
      pressed={pressed}
      defaultPressed={defaultPressed}
      onPressedChange={onPressedChange}
      disabled={disabled}
      className={cn(uiToggleVariants({ variant, size }), className)}
      {...props}
    >
      {children}
    </Toggle>
  )
})

UIToggle.displayName = "UIToggle"

// ============================================================================
// Preset Components
// ============================================================================

export interface UIToggleIconProps extends Omit<UIToggleProps, 'children' | 'icon'> {
  /**
   * 아이콘 컴포넌트
   */
  icon: LucideIcon
  /**
   * 아이콘 라벨 (접근성용)
   */
  label?: string
}

/**
 * 아이콘 전용 토글
 */
export const UIToggleIcon = React.forwardRef<
  React.ComponentRef<typeof TogglePrimitive.Root>,
  UIToggleIconProps
>(({
  icon,
  label,
  ...props
}, ref) => {
  return (
    <UIToggle
      ref={ref}
      icon={icon}
      aria-label={label}
      {...props}
    />
  )
})

UIToggleIcon.displayName = "UIToggleIcon"

export interface UIToggleGroupProps extends VariantProps<typeof uiToggleVariants> {
  /**
   * 토글 그룹 타입
   */
  type: 'single' | 'multiple'
  /**
   * 선택된 값(들) - single일 때는 string, multiple일 때는 string[]
   */
  value?: string | string[]
  /**
   * 기본 선택된 값(들)
   */
  defaultValue?: string | string[]
  /**
   * 값 변경 콜백
   */
  onValueChange?: (value: string | string[]) => void
  /**
   * 토글 아이템들
   */
  items: Array<{
    value: string
    label: string
    icon?: LucideIcon
    disabled?: boolean
    'aria-label'?: string
  }>
  /**
   * 방향
   */
  orientation?: 'horizontal' | 'vertical'
  /**
   * 컨테이너 클래스명
   */
  className?: string
  /**
   * 비활성화 여부
   */
  disabled?: boolean
  /**
   * 루프 가능 여부 (키보드 네비게이션용)
   */
  loop?: boolean
}

/**
 * 다중 토글 그룹 (라디오/체크박스 스타일)
 */
export const UIToggleGroup = React.forwardRef<
  HTMLDivElement,
  UIToggleGroupProps
>(({
  type,
  value,
  defaultValue,
  onValueChange,
  items,
  orientation = 'horizontal',
  variant,
  size,
  className,
  disabled,
  loop = true,
  ...props
}, ref) => {
  const [internalValue, setInternalValue] = React.useState<string | string[]>(
    defaultValue || (type === 'multiple' ? [] : '')
  )

  const isControlled = value !== undefined
  const currentValue = isControlled ? value : internalValue

  const handleToggle = (itemValue: string) => {
    if (disabled) return

    let newValue: string | string[]

    if (type === 'multiple') {
      const currentArray = Array.isArray(currentValue) ? currentValue : []
      newValue = currentArray.includes(itemValue)
        ? currentArray.filter(v => v !== itemValue)
        : [...currentArray, itemValue]
    } else {
      newValue = currentValue === itemValue ? '' : itemValue
    }

    if (!isControlled) {
      setInternalValue(newValue)
    }
    onValueChange?.(newValue)
  }

  const isPressed = (itemValue: string) => {
    if (type === 'multiple') {
      return Array.isArray(currentValue) && currentValue.includes(itemValue)
    }
    return currentValue === itemValue
  }

  return (
    <UIBox
      ref={ref}
      role="group"
      className={cn(
        "flex",
        orientation === 'horizontal' ? "flex-row gap-1" : "flex-col gap-1",
        className
      )}
      {...props}
    >
      {items.map((item, index) => (
        <UIToggle
          key={item.value}
          pressed={isPressed(item.value)}
          onPressedChange={() => handleToggle(item.value)}
          disabled={disabled || item.disabled}
          variant={variant}
          size={size}
          icon={item.icon}
          aria-label={item['aria-label'] || item.label}
          className={cn(
            // 첫 번째/마지막 아이템 라운딩 처리
            orientation === 'horizontal' && [
              index === 0 && "rounded-r-none",
              index === items.length - 1 && "rounded-l-none",
              index > 0 && index < items.length - 1 && "rounded-none",
              index > 0 && "border-l-0", // 테두리 중복 방지
            ],
            orientation === 'vertical' && [
              index === 0 && "rounded-b-none",
              index === items.length - 1 && "rounded-t-none",
              index > 0 && index < items.length - 1 && "rounded-none",
              index > 0 && "border-t-0", // 테두리 중복 방지
            ]
          )}
        >
          {item.label}
        </UIToggle>
      ))}
    </UIBox>
  )
})

UIToggleGroup.displayName = "UIToggleGroup"

export interface UIToggleThemeProps extends Omit<UIToggleProps, 'children' | 'pressed' | 'onPressedChange'> {
  /**
   * 현재 테마
   */
  theme?: 'light' | 'dark'
  /**
   * 테마 변경 콜백
   */
  onThemeChange?: (theme: 'light' | 'dark') => void
  /**
   * 라이트 모드 아이콘
   */
  lightIcon?: LucideIcon
  /**
   * 다크 모드 아이콘
   */
  darkIcon?: LucideIcon
  /**
   * 라벨 표시 여부
   */
  showLabel?: boolean
}

/**
 * 다크/라이트 모드 토글
 */
export const UIToggleTheme = React.forwardRef<
  React.ComponentRef<typeof TogglePrimitive.Root>,
  UIToggleThemeProps
>(({
  theme,
  onThemeChange,
  lightIcon,
  darkIcon,
  showLabel = false,
  variant = 'outline',
  ...props
}, ref) => {
  const [internalTheme, setInternalTheme] = React.useState<'light' | 'dark'>('light')

  const isControlled = theme !== undefined
  const currentTheme = isControlled ? theme : internalTheme
  const isDark = currentTheme === 'dark'

  const handleToggle = () => {
    const newTheme = isDark ? 'light' : 'dark'
    if (!isControlled) {
      setInternalTheme(newTheme)
    }
    onThemeChange?.(newTheme)
  }

  // 기본 아이콘들
  const LightIcon = lightIcon || Sun
  const DarkIcon = darkIcon || Moon

  return (
    <UIToggle
      ref={ref}
      pressed={isDark}
      onPressedChange={handleToggle}
      variant={variant}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      {...props}
    >
      {isDark ? <DarkIcon className="h-4 w-4" /> : <LightIcon className="h-4 w-4" />}
      {showLabel && (
        <UITypography as="span" variant="p" size="sm" className="ml-2">
          {isDark ? 'Dark' : 'Light'}
        </UITypography>
      )}
    </UIToggle>
  )
})

UIToggleTheme.displayName = "UIToggleTheme"

// ============================================================================
// Type Re-exports
// ============================================================================
// Note: All types are automatically exported via their interface declarations

export { uiToggleVariants }